/*
  Gorgias live chat renders its launcher and conversation window in same-origin
  srcdoc iframes (#chat-button, #chat-window). The app ships focus rings that fail
  WCAG 1.4.11 (white 3px ring on a cream page; semi-transparent blue in the dialog),
  and the theme cannot reach inside the iframes with page CSS, so we inject a
  stylesheet into each one. If Gorgias ever moves these iframes cross-origin the
  contentDocument access throws and this quietly does nothing.
*/
(function () {
  var STYLE_ID = 'stash-gorgias-a11y';
  var RING_DARK = '#262018';
  var RING_BLUE = '#1e40af';
  // #570: Gorgias draws its pills and icon buttons as translucent white with a #ebecef border on a light grey
  // gradient (about 1.1:1). This is >=3:1 against the lightest and darkest gradient stops and against white.
  var EDGE = '#5f5a52';
  // #553: the focusable conversation region has been seen without a name (it is named only in some chat states).
  var REGION_LABEL = 'Conversation and message options';

  var CSS = {
    'chat-button':
      '#gorgias-chat-messenger-button:focus-visible{' +
      'outline:3px solid ' + RING_DARK + '!important;' +
      'outline-offset:2px!important;' +
      'box-shadow:0 0 0 2px #fff,0 2px 4px rgba(22,22,22,.16)!important}',
    // #455: two-tone ring (white inner gap + solid #1e40af outer 2px) so it reads against both the light widget
    // gradient and any dark surface, and does not depend on the translucent --focus-color Gorgias ships.
    // Selectors are deliberately broad (inputs, links, tabindex, tabs) so states that only render when chat is
    // online (composer, quick replies) pick the ring up too.
    'chat-window':
      '#mountHere [role="dialog"]{--focus-color:' + RING_BLUE + '!important}' +
      '#mountHere :is(a,button,input,textarea,select,summary,[role="button"],[role="link"],[role="tab"],[role="menuitem"],[tabindex]:not([tabindex="-1"]),[contenteditable="true"]):focus-visible,' +
      '#mountHere :is(.form-control,.form-select,.form-check-input):focus{' +
      'outline:2px solid ' + RING_BLUE + '!important;outline-offset:2px!important;' +
      'box-shadow:0 0 0 2px #fff!important}' +
      // :not(#stash-ring) only lifts specificity above the broad rule when a region also carries a tabindex.
      // Inset variant for large containers whose outer ring would be clipped by the iframe edge or a scroller.
      '#mountHere :is([role="region"],[role="dialog"]):focus-visible:not(#stash-ring){' +
      'outline:3px solid ' + RING_BLUE + '!important;outline-offset:-3px!important;' +
      'box-shadow:inset 0 0 0 5px #fff!important}' +
      '#mountHere :is(button,[role="button"]){border-color:' + EDGE + '!important}'
  };

  function nameRegions(doc) {
    doc.querySelectorAll('[role="region"]:not([aria-label]):not([aria-labelledby])').forEach(function (el) {
      el.setAttribute('aria-label', REGION_LABEL);
    });
  }

  function ensure(iframe) {
    var css = CSS[iframe.id];
    var doc;
    try {
      doc = iframe.contentDocument;
    } catch (e) {
      return;
    }
    if (!css || !doc || !doc.documentElement) return;
    observe(iframe, doc);
    if (iframe.id === 'chat-window') nameRegions(doc);
    if (doc.getElementById(STYLE_ID)) return;
    var style = doc.createElement('style');
    style.id = STYLE_ID;
    style.textContent = css;
    (doc.head || doc.documentElement).appendChild(style);
  }

  var observedDocs = typeof WeakSet === 'function' ? new WeakSet() : null;

  // Gorgias can swap the iframe's document after we first look, so the observer is tied to the current document
  // (re-checked on every ensure) rather than set up once.
  function observe(iframe, doc) {
    if (!observedDocs || observedDocs.has(doc)) return;
    observedDocs.add(doc);
    new MutationObserver(function () {
      ensure(iframe);
    }).observe(doc.documentElement, { childList: true, subtree: true });
  }

  function watch(iframe) {
    if (iframe.dataset.stashA11yWatched) return;
    iframe.dataset.stashA11yWatched = 'true';

    ensure(iframe);
    iframe.addEventListener('load', function () {
      ensure(iframe);
    });
    // Healing path if Gorgias rewrites the iframe document without a load event.
    iframe.addEventListener('focus', function () {
      ensure(iframe);
    });
  }

  function scan() {
    document.querySelectorAll('iframe#chat-button, iframe#chat-window').forEach(watch);
  }

  var scanQueued = false;
  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(function () {
      scanQueued = false;
      scan();
    });
  }

  scan();
  new MutationObserver(queueScan).observe(document.documentElement, { childList: true, subtree: true });
})();
